import React, { useState, useEffect, useRef } from 'react';
import {
    Building,
    Phone,
    Globe,
    MapPin,
    Clock,
    Wrench,
    Car,
    Save,
    Trash2,
    X,
    AlertTriangle,
    Check,
    CreditCard,
    RefreshCw,
    Landmark,
    Camera,
    Upload,
    Loader
} from 'lucide-react';
import { authService } from '../../services/authService';
import { format } from 'date-fns';
import { fr, enUS, ar } from 'date-fns/locale';
import { GarageProfileUpdateData, VehicleType, Speciality} from '../../types/auth';
import { Bell } from 'lucide-react';
import { ChangePassword } from '../Auth/ChangePassword';
import { useNavigate } from "react-router-dom";
import { useLanguage } from '../../hooks/useLanguage';

interface BusinessHours {
    open: string;
    close: string;
    closed: boolean;
}

interface Address {
    street: string;
    zipCode: string;
    city: string;
    country: string;
    latitude: number;
    longitude: number;
}

interface SettingsGarageUser {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    garageName: string;
    siretNumber: string;
    address: Address;
    garagePhone: string;
    isNotificationsEnabled: boolean;
    website: string;
    iban: string;
    swift: string;
    specialities: string[];
    vehicleTypes: string[];
    businessHours: {
        MONDAY: BusinessHours;
    };
    logo?: { url: string; uploadedAt?: Date };
    garagePhotos?: { url: string; description?: string; uploadedAt?: Date }[];
}

interface SettingsProps {
    creditBalance?: { standard: number; ai: number } | null;
    onCreditUpdate?: () => Promise<void>;
}

export const Settings: React.FC<SettingsProps> = ({ creditBalance, onCreditUpdate }) => {
    const { t, currentLanguage, isRTL } = useLanguage();
    const [user, setUser] = useState<SettingsGarageUser>(() => {
        const stored = JSON.parse(localStorage.getItem('user') || '{}');
        return {
            ...stored,
            iban: stored.iban || '',
            swift: stored.swift || ''
        };
    });
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const navigate = useNavigate();
    const [notificationLoading, setNotificationLoading] = useState(false);

    // Image states
    const [logoUploading, setLogoUploading] = useState(false);
    const [photosUploading, setPhotosUploading] = useState(false);
    const [imageError, setImageError] = useState<string | null>(null);
    const logoInputRef = useRef<HTMLInputElement>(null);
    const photosInputRef = useRef<HTMLInputElement>(null);

    const [showNotifications, setShowNotifications] = useState(() => {
        return localStorage.getItem('showNotifications') !== 'false';
    });

    useEffect(() => {
        localStorage.setItem('showNotifications', showNotifications.toString());
    }, [showNotifications]);

    const getDateLocale = () => {
        switch (currentLanguage) {
            case 'fr': return fr;
            case 'en': return enUS;
            default: return ar;
        }
    };

    const specialityOptions = [
        { value: 'MECANIQUE_GENERALE', label: t('register.specialties.options.MECANIQUE_GENERALE') },
        { value: 'CARROSSERIE', label: t('register.specialties.options.CARROSSERIE') },
        { value: 'CLIMATISATION', label: t('register.specialties.options.CLIMATISATION') },
        { value: 'DIAGNOSTIC_ELECTRONIQUE', label: t('register.specialties.options.DIAGNOSTIC_ELECTRONIQUE') },
        { value: 'PNEUMATIQUES', label: t('register.specialties.options.PNEUMATIQUES') },
        { value: 'VIDANGE', label: t('register.specialties.options.VIDANGE') },
        { value: 'FREINAGE', label: t('register.specialties.options.FREINAGE') }
    ];

    const vehicleTypeOptions = [
        { value: 'ALL_VEHICLE', label: t('register.vehicleTypes.options.ALL_VEHICLE') },
        { value: 'TOYOTA', label: t('register.vehicleTypes.options.TOYOTA') },
        { value: 'HYUNDAI', label: t('register.vehicleTypes.options.HYUNDAI') },
        { value: 'FORD', label: t('register.vehicleTypes.options.FORD') },
        { value: 'MAZDA', label: t('register.vehicleTypes.options.MAZDA') },
        { value: 'KOREAN', label: t('register.vehicleTypes.options.KOREAN') },
        { value: 'AMERICAN', label: t('register.vehicleTypes.options.AMERICAN') },
        { value: 'CHINESE', label: t('register.vehicleTypes.options.CHINESE') },
        { value: 'CHANGAN', label: t('register.vehicleTypes.options.CHANGAN') },
        { value: 'GERMAN', label: t('register.vehicleTypes.options.GERMAN') }
    ];

    const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            setImageError(t('settings.images.invalidType'));
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            setImageError(t('settings.images.fileTooLarge'));
            return;
        }

        try {
            setLogoUploading(true);
            setImageError(null);
            const response = await authService.updateGarageLogo(user._id, file);

            const updatedUser = { ...user, logo: response.logo };
            setUser(updatedUser);
            localStorage.setItem('user', JSON.stringify(updatedUser));
            window.dispatchEvent(new StorageEvent('storage', {
                key: 'user',
                newValue: JSON.stringify(updatedUser)
            }));

            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);
        } catch (error: any) {
            setImageError(error.message || t('settings.images.uploadError'));
        } finally {
            setLogoUploading(false);
            if (logoInputRef.current) logoInputRef.current.value = '';
        }
    };

    const handlePhotosUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        const currentCount = user.garagePhotos?.length || 0;
        if (currentCount + files.length > 3) {
            setImageError(t('settings.images.maxPhotos'));
            return;
        }

        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        const validFiles: File[] = [];
        for (let i = 0; i < files.length; i++) {
            if (!allowedTypes.includes(files[i].type) || files[i].size > 5 * 1024 * 1024) continue;
            validFiles.push(files[i]);
        }

        if (validFiles.length === 0) {
            setImageError(t('settings.images.noValidFiles'));
            return;
        }

        try {
            setPhotosUploading(true);
            setImageError(null);
            const response = await authService.addGaragePhotos(user._id, validFiles);

            const updatedUser = { ...user, garagePhotos: response.garagePhotos };
            setUser(updatedUser);
            localStorage.setItem('user', JSON.stringify(updatedUser));
            window.dispatchEvent(new StorageEvent('storage', {
                key: 'user',
                newValue: JSON.stringify(updatedUser)
            }));

            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);
        } catch (error: any) {
            setImageError(error.message || t('settings.images.uploadError'));
        } finally {
            setPhotosUploading(false);
            if (photosInputRef.current) photosInputRef.current.value = '';
        }
    };

    const handleDeletePhoto = async (index: number) => {
        if ((user.garagePhotos?.length || 0) <= 1) {
            setImageError(t('settings.images.minPhotoRequired'));
            return;
        }
        try {
            setImageError(null);
            const response = await authService.deleteGaragePhoto(user._id, index);

            const updatedUser = { ...user, garagePhotos: response.garagePhotos };
            setUser(updatedUser);
            localStorage.setItem('user', JSON.stringify(updatedUser));
            window.dispatchEvent(new StorageEvent('storage', {
                key: 'user',
                newValue: JSON.stringify(updatedUser)
            }));
        } catch (error: any) {
            setImageError(error.message || t('settings.images.deleteError'));
        }
    };

    const handleNotificationToggle = async (enabled: boolean) => {
        setNotificationLoading(true);
        try {
            const response = await authService.updateNotificationSettings(user._id, enabled);

            if (response.success && response.garage) {
                const updatedUser = {
                    ...user,
                    ...response.garage,
                    isNotificationsEnabled: enabled
                };
                setShowNotifications(updatedUser.isNotificationsEnabled);
                setUser(updatedUser);
                localStorage.setItem('user', JSON.stringify(updatedUser));
                window.dispatchEvent(new StorageEvent('storage', {
                    key: 'user',
                    newValue: JSON.stringify(updatedUser)
                }));
                setSuccess(true);
                setTimeout(() => setSuccess(false), 3000);
            }
        } catch (error: any) {
            console.error('Error updating notifications:', error);
            setUser({ ...user, isNotificationsEnabled: !enabled });
            alert(error.message || 'Erreur lors de la mise à jour des notifications');
        } finally {
            setNotificationLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            const updateData: GarageProfileUpdateData = {
                phoneNumber: user.phoneNumber,
                garageName: user.garageName,
                siretNumber: user.siretNumber,
                garagePhone: user.garagePhone,
                isNotificationEnabled: user.isNotificationsEnabled,
                iban: user.iban.trim(),
                swift: user.swift.trim(),
                website: user.website,
                address: user.address,
                specialities: user.specialities as Speciality[],
                vehicleTypes: user.vehicleTypes as VehicleType[],
                businessHours: user.businessHours
            };

            const response = await authService.updateGarageProfile(user._id, updateData);

            const updatedUser = {
                ...response.user,
                iban: response.user.iban || '',
                swift: response.user.swift || ''
            };
            setUser(updatedUser);
            localStorage.setItem('user', JSON.stringify(updatedUser));
            window.dispatchEvent(new StorageEvent('storage', {
                key: 'user',
                newValue: JSON.stringify(updatedUser)
            }));

            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);
        } catch (error) {
            console.error('Error during update:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleShowDeleteConfirm = () => {
        setShowDeleteConfirm(true);
        setDeleteError(null);
    };

    const handleDeleteAccount = async () => {
        setDeleteLoading(true);
        setDeleteError(null);
        try {
            await authService.deleteAccountGarage();
            authService.logout();
            navigate('/', { replace: true });
        } catch (error: any) {
            console.error("Error during account deletion:", error);
            setDeleteError(error.message || t('settings.errors.deleteAccountError'));
            setDeleteLoading(false);
        }
    };

    const handleSpecialityChange = (value: string) => {
        const newSpecialities = user.specialities.includes(value)
            ? user.specialities.filter(s => s !== value)
            : [...user.specialities, value];
        setUser({ ...user, specialities: newSpecialities });
    };

    const handleVehicleTypeChange = (value: string) => {
        const newTypes = user.vehicleTypes.includes(value)
            ? user.vehicleTypes.filter(t => t !== value)
            : [...user.vehicleTypes, value];
        setUser({ ...user, vehicleTypes: newTypes });
    };

    const isBankDetailsIncomplete = !(user.iban?.trim()) || !(user.swift?.trim());

    return (
        <div className={`p-6 max-w-7xl mx-auto ${isRTL ? 'rtl' : 'ltr'}`}>
            <div className={`flex justify-between items-center mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <h2 className="text-2xl font-bold text-gray-900">{t('settings.title')}</h2>
                {success && (
                    <div className={`flex items-center text-green-600 bg-green-50 px-4 py-2 rounded-lg ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <Check className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                        {t('settings.changesSaved')}
                    </div>
                )}
            </div>

            {/* Account deletion confirmation modal */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl shadow-lg max-w-md w-full mx-4 p-6">
                        <div className={`flex items-center mb-4 text-red-600 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <AlertTriangle className={`w-6 h-6 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                            <h3 className="text-xl font-bold">{t('settings.modals.deleteAccount.title')}</h3>
                        </div>
                        <p className="mb-4 text-gray-700 font-bold">{t('settings.modals.deleteAccount.warning')}</p>
                        <p className="mb-4 text-gray-700">{t('settings.modals.deleteAccount.confirmation')}</p>
                        {deleteError && (
                            <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg">{deleteError}</div>
                        )}
                        <div className={`flex justify-end space-x-3 ${isRTL ? 'space-x-reverse' : ''}`}>
                            <button
                                onClick={() => setShowDeleteConfirm(false)}
                                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                                disabled={deleteLoading}
                            >
                                {t('settings.modals.deleteAccount.cancel')}
                            </button>
                            <button
                                onClick={handleDeleteAccount}
                                className={`px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}
                                disabled={deleteLoading}
                            >
                                {deleteLoading ? (
                                    <>
                                        <RefreshCw className={`w-4 h-4 animate-spin ${isRTL ? 'ml-2' : 'mr-2'}`} />
                                        {t('settings.modals.deleteAccount.deleting')}
                                    </>
                                ) : (
                                    t('settings.modals.deleteAccount.delete')
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="space-y-6">

                {/* SECTION IMAGES — Logo + Photos (upload immédiat) */}
                <div className="bg-white rounded-xl shadow-md p-6 space-y-6 border-2 border-indigo-100">
                    <h3 className={`text-lg font-semibold text-gray-900 border-b pb-3 flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <Camera className={`h-5 w-5 text-indigo-500 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                        {t('settings.images.title')}
                    </h3>

                    {imageError && (
                        <div className={`flex items-center p-3 bg-red-50 border border-red-200 rounded-lg ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <AlertTriangle className={`h-4 w-4 text-red-500 flex-shrink-0 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                            <p className="text-sm text-red-700 flex-1">{imageError}</p>
                            <button onClick={() => setImageError(null)} className={`${isRTL ? 'mr-auto' : 'ml-auto'}`}>
                                <X className="h-4 w-4 text-red-400 hover:text-red-600" />
                            </button>
                        </div>
                    )}

                    {/* Logo */}
                    <div>
                        <label className={`block text-sm font-medium text-gray-700 mb-3 ${isRTL ? 'text-right' : 'text-left'}`}>
                            {t('settings.images.logo')}
                        </label>

                        <div className={`flex items-start gap-5 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            {user.logo?.url ? (
                                <div className="w-24 h-24 rounded-xl overflow-hidden border-2 border-indigo-200 shadow-sm flex-shrink-0">
                                    <img src={user.logo.url} alt="Logo" className="w-full h-full object-cover" />
                                </div>
                            ) : (
                                <div className="w-24 h-24 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50 flex-shrink-0">
                                    <Camera className="w-8 h-8 text-gray-400" />
                                </div>
                            )}

                            <div>
                                <button
                                    type="button"
                                    onClick={() => logoInputRef.current?.click()}
                                    disabled={logoUploading}
                                    className={`flex items-center gap-2 px-4 py-2 border-2 border-indigo-500 text-indigo-600 rounded-full hover:bg-indigo-50 transition-all font-medium text-sm disabled:opacity-50 ${isRTL ? 'flex-row-reverse' : ''}`}
                                >
                                    {logoUploading ? (
                                        <>
                                            <Loader className="w-4 h-4 animate-spin" />
                                            {t('settings.images.uploading')}
                                        </>
                                    ) : (
                                        <>
                                            <Upload size={15} />
                                            {user.logo?.url ? t('settings.images.changeLogo') : t('settings.images.addLogo')}
                                        </>
                                    )}
                                </button>
                                <p className={`mt-2 text-xs text-gray-500 ${isRTL ? 'text-right' : 'text-left'}`}>
                                    {t('settings.images.logoHelp')}
                                </p>
                            </div>
                        </div>

                        <input
                            ref={logoInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/gif,image/webp"
                            onChange={handleLogoUpload}
                            className="hidden"
                        />
                    </div>

                    <div className="border-t border-gray-100" />

                    {/* Photos */}
                    <div>
                        <label className={`block text-sm font-medium text-gray-700 mb-3 ${isRTL ? 'text-right' : 'text-left'}`}>
                            {t('settings.images.photos')}
                            <span className="text-gray-400 font-normal ml-1">({user.garagePhotos?.length || 0}/3)</span>
                        </label>

                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                            {user.garagePhotos?.map((photo, index) => {
                                const isLast = (user.garagePhotos?.length || 0) <= 1;
                                return (
                                    <div key={index} className="relative aspect-video rounded-xl overflow-hidden border-2 border-indigo-100 shadow-sm group">
                                        <img src={photo.url} alt={`Photo ${index + 1}`} className="w-full h-full object-cover" />
                                        <button
                                            type="button"
                                            onClick={() => handleDeletePhoto(index)}
                                            disabled={isLast}
                                            title={isLast ? t('settings.images.minPhotoRequired') : t('settings.photos.deletePhoto')}
                                            className={`absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center shadow-md transition-opacity ${isLast ? 'bg-gray-400 opacity-40 cursor-not-allowed group-hover:opacity-40' : 'bg-red-500 text-white opacity-0 group-hover:opacity-100 hover:bg-red-600'}`}
                                        >
                                            <X size={13} />
                                        </button>
                                    </div>
                                );
                            })}

                            {(!user.garagePhotos || user.garagePhotos.length < 3) && (
                                <div
                                    onClick={() => !photosUploading && photosInputRef.current?.click()}
                                    className="aspect-video cursor-pointer border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center gap-2 hover:border-indigo-400 hover:bg-indigo-50 transition-all"
                                >
                                    {photosUploading ? (
                                        <Loader className="w-6 h-6 text-indigo-500 animate-spin" />
                                    ) : (
                                        <>
                                            <Upload className="w-5 h-5 text-gray-400" />
                                            <span className="text-xs text-gray-500 font-medium">
                                                {t('settings.images.addPhoto')}
                                            </span>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                        <input
                            ref={photosInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/gif,image/webp"
                            multiple
                            onChange={handlePhotosUpload}
                            className="hidden"
                        />

                        <p className={`mt-3 text-xs text-gray-400 ${isRTL ? 'text-right' : 'text-left'}`}>
                            {t('settings.images.photosHelp')}
                        </p>
                    </div>
                </div>

                {/* Notification Preferences */}
                <div className="bg-white rounded-xl shadow-md p-6 space-y-6 border-2 border-indigo-100">
                    <h3 className={`text-lg font-semibold text-gray-900 border-b pb-3 flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <Bell className={`h-5 w-5 text-indigo-500 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                        {t('settings.notifications.title')}
                    </h3>

                    <div className="space-y-4">
                        <div className={`flex items-center justify-between p-4 bg-gray-50 rounded-lg ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <div>
                                <h4 className="font-medium text-gray-900">{t('settings.notifications.showNotifications')}</h4>
                                <p className="text-sm text-gray-500 mt-1">{t('settings.notifications.description')}</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={user.isNotificationsEnabled}
                                    onChange={(e) => handleNotificationToggle(e.target.checked)}
                                    disabled={notificationLoading}
                                    className="sr-only peer"
                                />
                                <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600 ${notificationLoading ? 'opacity-50 cursor-not-allowed' : ''}`}></div>
                            </label>
                        </div>
                        {notificationLoading && (
                            <p className="text-sm text-gray-500 text-center">
                                {t('settings.notifications.updating')}
                            </p>
                        )}
                    </div>
                </div>

                {/* Change Password */}
                <ChangePassword userId={user._id} />

                {/* Main form */}
                <form onSubmit={handleSubmit} className="space-y-6">

                    {/* Personal Information */}
                    <div className="bg-white rounded-xl shadow-sm p-6 space-y-6">
                        <h3 className="text-lg font-medium text-gray-900 border-b pb-3">
                            {t('settings.personalInfo.title')}
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">{t('settings.personalInfo.firstName')}</label>
                                <input type="text" value={user.firstName} readOnly
                                    className="mt-1 block w-full bg-gray-50 border border-gray-300 rounded-lg px-3 py-2" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">{t('settings.personalInfo.lastName')}</label>
                                <input type="text" value={user.lastName} readOnly
                                    className="mt-1 block w-full bg-gray-50 border border-gray-300 rounded-lg px-3 py-2" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">{t('settings.personalInfo.email')}</label>
                                <input type="email" value={user.email} readOnly
                                    className="mt-1 block w-full bg-gray-50 border border-gray-300 rounded-lg px-3 py-2" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">{t('settings.personalInfo.personalPhone')}</label>
                                <div className="mt-1 relative rounded-lg shadow-sm">
                                    <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`}>
                                        <Phone className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="tel"
                                        value={user.phoneNumber}
                                        onChange={e => setUser({ ...user, phoneNumber: e.target.value })}
                                        className={`block w-full ${isRTL ? 'pr-10' : 'pl-10'} border border-gray-300 rounded-lg px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500`}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Garage Information */}
                    <div className="bg-white rounded-xl shadow-sm p-6 space-y-6">
                        <h3 className="text-lg font-medium text-gray-900 border-b pb-3">
                            {t('settings.garageInfo.title')}
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">{t('settings.garageInfo.garageName')}</label>
                                <div className="mt-1 relative rounded-lg shadow-sm">
                                    <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`}>
                                        <Building className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="text"
                                        value={user.garageName}
                                        onChange={e => setUser({ ...user, garageName: e.target.value })}
                                        className={`block w-full ${isRTL ? 'pr-10' : 'pl-10'} border border-gray-300 rounded-lg px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500`}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">{t('settings.garageInfo.siret')}</label>
                                <input
                                    type="text"
                                    value={user.siretNumber}
                                    onChange={e => setUser({ ...user, siretNumber: e.target.value })}
                                    className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">{t('settings.garageInfo.garagePhone')}</label>
                                <div className="mt-1 relative rounded-lg shadow-sm">
                                    <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`}>
                                        <Phone className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="tel"
                                        value={user.garagePhone}
                                        onChange={e => setUser({ ...user, garagePhone: e.target.value })}
                                        className={`block w-full ${isRTL ? 'pr-10' : 'pl-10'} border border-gray-300 rounded-lg px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500`}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">{t('settings.garageInfo.website')}</label>
                                <div className="mt-1 relative rounded-lg shadow-sm">
                                    <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`}>
                                        <Globe className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="url"
                                        value={user.website}
                                        onChange={e => setUser({ ...user, website: e.target.value })}
                                        className={`block w-full ${isRTL ? 'pr-10' : 'pl-10'} border border-gray-300 rounded-lg px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500`}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Address */}
                    <div className="bg-white rounded-xl shadow-sm p-6 space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
                        <h3 className={`text-lg font-medium text-gray-900 border-b pb-3 flex items-center gap-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                            <MapPin className="h-5 w-5" />
                            {t('settings.address.title')}
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700">{t('settings.address.street')}</label>
                                <input
                                    type="text"
                                    value={user.address.street}
                                    onChange={e => setUser({ ...user, address: { ...user.address, street: e.target.value } })}
                                    className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">{t('settings.address.zipCode')}</label>
                                <input
                                    type="text"
                                    value={user.address.zipCode}
                                    onChange={e => setUser({ ...user, address: { ...user.address, zipCode: e.target.value } })}
                                    className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">{t('settings.address.city')}</label>
                                <input
                                    type="text"
                                    value={user.address.city}
                                    onChange={e => setUser({ ...user, address: { ...user.address, city: e.target.value } })}
                                    className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Specialties and Vehicle Types */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-white rounded-xl shadow-sm p-6" dir={isRTL ? 'rtl' : 'ltr'}>
                            <h3 className={`text-lg font-medium text-gray-900 border-b pb-3 mb-4 flex items-center gap-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                                <Wrench className="h-5 w-5" />
                                {t('settings.specialties.title')}
                            </h3>
                            <div className="space-y-3">
                                {specialityOptions.map(option => (
                                    <label key={option.value} className="flex items-center gap-2" dir={isRTL ? 'rtl' : 'ltr'}>
                                        <input
                                            type="checkbox"
                                            checked={user.specialities.includes(option.value)}
                                            onChange={() => handleSpecialityChange(option.value)}
                                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                        />
                                        <span className="text-sm text-gray-700">{option.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        <div className="bg-white rounded-xl shadow-sm p-6" dir={isRTL ? 'rtl' : 'ltr'}>
                            <h3 className={`text-lg font-medium text-gray-900 border-b pb-3 mb-4 flex items-center gap-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                                <Car className="h-5 w-5" />
                                {t('settings.vehicleTypes.title')}
                            </h3>
                            <div className="space-y-3">
                                {vehicleTypeOptions.map(option => (
                                    <label key={option.value} className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            checked={user.vehicleTypes.includes(option.value)}
                                            onChange={() => handleVehicleTypeChange(option.value)}
                                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                        />
                                        <span className="text-sm text-gray-700">{option.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Save / Delete buttons */}
                    <div className={`flex justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <button
                            type="button"
                            onClick={handleShowDeleteConfirm}
                            className={`flex items-center px-4 py-2 border border-red-300 text-red-700 bg-white hover:bg-red-50 rounded-lg transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
                        >
                            <Trash2 className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                            {t('settings.actions.deleteAccount')}
                        </button>

                        <button
                            type="submit"
                            disabled={loading}
                            className={`flex items-center px-6 py-3 border border-transparent rounded-lg shadow-sm text-white bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-150 ${isRTL ? 'flex-row-reverse' : ''}`}
                        >
                            {loading ? (
                                <>
                                    <svg className={`animate-spin h-5 w-5 text-white ${isRTL ? 'ml-3' : '-ml-1 mr-3'}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    {t('settings.actions.saving')}
                                </>
                            ) : (
                                <>
                                    <Save className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                                    {t('settings.actions.save')}
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};
